import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, HelpCircle, Send, MessageSquare } from 'lucide-react';
import NavbarLanding from '../components/NavbarLanding';
import Navigation from '../components/Navigation';
import { UserContext } from '../store/useStore';
import { db } from '../lib/firebase';
import { ref, get, set, update } from 'firebase/database';
import { API_BASE_URL } from '../config/api';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { AudioRecorder } from '../components/AudioRecorder';

export default function NewsActivities() {
  const location = useLocation();
  const navigate = useNavigate();
  const { article } = location.state || {};
  // UserContext available if needed in future
  
  // Game states
  const [currentStep, setCurrentStep] = useState<'mcq' | 'fill' | 'chat'>('mcq');
  const [score, setScore] = useState(0);
  const [mcqIndex, setMcqIndex] = useState(0);
  const [fillIndex, setFillIndex] = useState(0);
  
  // Answers
  const [mcqSelected, setMcqSelected] = useState<string | null>(null);
  const [fillAnswer, setFillAnswer] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  
  // Chat
  const [chatMessages, setChatMessages] = useState<{role: string, content: string}[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatting, setIsChatting] = useState(false);

  // Speech Recognition
  const { isRecording, transcript, startRecording, stopRecording } = useSpeechRecognition({
    onSendMessage: (msg) => handleChat(msg),
    isFromChatInput: true,
    isFromPractice: false
  });

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  useEffect(() => {
    if (transcript && !isRecording) {
      handleChat(transcript);
    }
  }, [transcript, isRecording]);

  useEffect(() => {
    if (!article) {
      navigate('/news');
    }
  }, [article, navigate]);

  const resolveCorrectAnswer = (mcq: any): string => {
    const answer = mcq.correctAnswer;
    const letterMap: Record<string, number> = { 'A': 0, 'B': 1, 'C': 2, 'D': 3 };
    // If the answer is a single letter A-D, resolve it to the actual option text
    if (answer && answer.length === 1 && letterMap[answer.toUpperCase()] !== undefined) {
      const idx = letterMap[answer.toUpperCase()];
      if (mcq.options[idx]) return mcq.options[idx];
    }
    return answer;
  };

  const handleMcqSubmit = () => {
    if (!mcqSelected) return;
    const currentMcq = article.multipleChoice[mcqIndex];
    const correctAnswer = resolveCorrectAnswer(currentMcq);
    const isCorrect = mcqSelected === correctAnswer;
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    
    if (isCorrect) {
      setScore(s => s + 10);
      updateNewsLeaderboard(10);
    }
    
    setTimeout(() => {
      setFeedback(null);
      setMcqSelected(null);
      if (mcqIndex < article.multipleChoice.length - 1) {
        setMcqIndex(i => i + 1);
      } else {
        setCurrentStep('fill');
      }
    }, 1500);
  };

  const handleFillSubmit = () => {
    if (!fillAnswer) return;
    const currentQ = article.fillInTheBlank[fillIndex];
    // Simple exact match or case-insensitive match
    const isCorrect = fillAnswer.toLowerCase().trim() === currentQ.answer.toLowerCase().trim();
    
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    
    if (isCorrect) {
      const pointsEarned = showHint ? 5 : 10; // -5 penalty for hint
      setScore(s => s + pointsEarned);
      updateNewsLeaderboard(pointsEarned);
    }
    
    setTimeout(() => {
      setFeedback(null);
      setFillAnswer('');
      setShowHint(false);
      
      if (fillIndex < article.fillInTheBlank.length - 1) {
        setFillIndex(i => i + 1);
      } else {
        setCurrentStep('chat');
      }
    }, 1500);
  };

  const useHint = () => {
    if (!showHint) {
       setShowHint(true);
       // We don't deduct immediately, we just reward less on success.
       // Or we can deduct directly from global points here.
    }
  };

  const updateNewsLeaderboard = async (points: number) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const userObj = JSON.parse(userStr);
      const userId = userObj.uid;
      
      const scoreRef = ref(db, `leaderboards/news/${userId}`);
      const snapshot = await get(scoreRef);
      if (snapshot.exists()) {
         await update(scoreRef, { score: snapshot.val().score + points, lastUpdated: Date.now() });
      } else {
         await set(scoreRef, { 
           score: points, 
           userId: userId,
           displayName: userObj.displayName || userObj.email,
           photoURL: userObj.photoURL || '',
           lastUpdated: Date.now() 
         });
      }
    } catch(e) {
      console.error("Failed to update news leaderboard", e);
    }
  };

  const handleChat = async (messageOverride?: string) => {
    // If the event is passed instead of a string, ignore it
    const textToSend = typeof messageOverride === 'string' ? messageOverride : chatInput;
    if (!textToSend.trim() || isChatting) return;
    
    const newMessages = [...chatMessages, { role: 'user', content: textToSend }];
    setChatMessages(newMessages);
    setChatInput('');
    setIsChatting(true);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/news/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: newMessages,
          articleContext: article.content.substring(0, 500) // send a snippet for context
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setChatMessages([...newMessages, { role: 'assistant', content: data.reply }]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsChatting(false);
    }
  };

  if (!article) return null;

  return (
    <>
      <NavbarLanding isLanding={false} />
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <main className="flex-1 pb-16 md:pb-0 md:ml-64 p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            
            <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-800">News Activities</h2>
              <div className="text-purple-600 font-bold bg-purple-50 px-4 py-2 rounded-lg">
                Score: {score}
              </div>
            </div>

            {currentStep === 'mcq' && article.multipleChoice && (
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
                <div className="mb-6 flex justify-between items-center text-sm font-medium text-gray-500 uppercase tracking-wide">
                  <span>Question {mcqIndex + 1} of {article.multipleChoice.length}</span>
                  <span>Multiple Choice</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  {article.multipleChoice[mcqIndex].question}
                </h3>
                
                <div className="space-y-3">
                  {article.multipleChoice[mcqIndex].options.map((opt: string, i: number) => (
                    <button
                      key={i}
                      onClick={() => setMcqSelected(opt)}
                      disabled={feedback !== null}
                      className={`w-full p-4 rounded-xl text-left font-medium transition-all border-2
                        ${mcqSelected === opt ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}
                        ${feedback === 'correct' && mcqSelected === opt ? 'border-green-500 bg-green-50 text-green-700' : ''}
                        ${feedback === 'incorrect' && mcqSelected === opt ? 'border-red-500 bg-red-50 text-red-700' : ''}
                      `}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                
                {feedback && (
                  <div className={`mt-6 p-4 rounded-lg flex items-center gap-3 font-bold ${feedback === 'correct' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {feedback === 'correct' ? <CheckCircle2 className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
                    {feedback === 'correct' ? '+10 Points!' : `Incorrect! The correct answer is: ${resolveCorrectAnswer(article.multipleChoice[mcqIndex])}`}
                  </div>
                )}
                
                <button
                  onClick={handleMcqSubmit}
                  disabled={!mcqSelected || feedback !== null}
                  className="mt-6 w-full py-4 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  Submit Answer
                </button>
              </div>
            )}

            {currentStep === 'fill' && article.fillInTheBlank && (
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
                <div className="mb-6 flex justify-between items-center text-sm font-medium text-gray-500 uppercase tracking-wide">
                  <span>Question {fillIndex + 1} of {article.fillInTheBlank.length}</span>
                  <span>Fill in the Blank</span>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
                  <p className="text-blue-800 italic">{article.fillInTheBlank[fillIndex].translationPT}</p>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  {article.fillInTheBlank[fillIndex].sentence.replace('___', '______')}
                </h3>
                
                <input 
                    type="text"
                    value={fillAnswer}
                    onChange={(e) => setFillAnswer(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && fillAnswer && feedback === null) handleFillSubmit(); }}
                    disabled={feedback !== null}
                    placeholder="Type the missing word..."
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-purple-600 focus:ring-0 text-lg outline-none transition-colors"
                  />
                  <button 
                    onClick={useHint}
                    disabled={showHint || feedback !== null}
                    className="mt-2 p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg disabled:opacity-50 flex items-center gap-1 text-sm font-bold self-start"
                  >
                    <HelpCircle className="w-5 h-5" /> Hint (-5 pts)
                  </button>
                
                {showHint && (
                  <div className="mt-3 text-sm text-yellow-700 font-medium">
                    Hint: The word starts with "{article.fillInTheBlank[fillIndex].answer.charAt(0)}" and has {article.fillInTheBlank[fillIndex].answer.length} letters.
                  </div>
                )}
                
                {feedback && (
                  <div className={`mt-6 p-4 rounded-lg flex items-center gap-3 font-bold ${feedback === 'correct' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {feedback === 'correct' ? <CheckCircle2 className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
                    {feedback === 'correct' ? (showHint ? '+5 Points!' : '+10 Points!') : `Incorrect! Answer: ${article.fillInTheBlank[fillIndex].answer}`}
                  </div>
                )}
                
                <button
                  onClick={handleFillSubmit}
                  disabled={!fillAnswer || feedback !== null}
                  className="mt-6 w-full py-4 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  Submit Answer
                </button>
              </div>
            )}

            {currentStep === 'chat' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{ height: '70vh' }}>
                <div className="p-4 border-b border-gray-100 bg-purple-50 flex items-center gap-3">
                  <MessageSquare className="text-purple-600 w-6 h-6" />
                  <div>
                    <h3 className="font-bold text-gray-900">Discuss with AI Teacher</h3>
                    <p className="text-xs text-gray-600">Ask questions about the article!</p>
                  </div>
                </div>
                
                <div className="flex-1 p-4 overflow-y-auto space-y-4">
                  <div className="flex justify-start">
                    <div className="bg-gray-100 text-gray-800 px-4 py-3 rounded-2xl rounded-tl-none max-w-[85%]">
                      Hello! What did you think about the article "{article.title}"? Do you have any questions about the vocabulary?
                    </div>
                  </div>
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`px-4 py-3 rounded-2xl max-w-[85%] ${msg.role === 'user' ? 'bg-purple-600 text-white rounded-tr-none' : 'bg-gray-100 text-gray-800 rounded-tl-none'}`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isChatting && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-500 px-4 py-3 rounded-2xl rounded-tl-none text-sm animate-pulse">
                        Typing...
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col gap-2">
                  {transcript && isRecording && (
                    <div className="w-full mb-2 p-2 bg-purple-50 text-purple-700 animate-pulse rounded-xl text-sm">
                      {transcript}
                    </div>
                  )}
                  <div className="flex gap-2 w-full">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleChat()}
                      placeholder="Type your message in English or Portuguese..."
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 outline-none"
                    />
                    <AudioRecorder 
                      isListening={isRecording}
                      onMicClick={handleMicClick}
                    />
                    <button
                      onClick={() => handleChat()}
                      disabled={isChatting || !chatInput.trim()}
                      className="p-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </>
  );
}
